import React, { useState, useEffect, useRef } from 'react';
import EmojiPicker from 'emoji-picker-react';
import styled, { createGlobalStyle } from 'styled-components';
import { useNavigate } from 'react-router-dom';

// --- SOUND IMPORT ---
import chatNotificationSound from './Notifications for chat.mp3';

// --- FIREBASE & ICONS ---
import { db, storage, auth } from './firebase';
import { onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection, query, onSnapshot, addDoc, serverTimestamp,
  getDocs, where, orderBy, doc, updateDoc, setDoc, increment, deleteDoc, writeBatch
} from "firebase/firestore";
import AppHeader from './AppHeader';

import { FaSearch, FaArrowLeft, FaPlus, FaUsers, FaCheckCircle, FaRegCircle, FaCamera, FaEllipsisV, FaTrash, FaUserShield, FaUserPlus, FaUserMinus, FaPaperclip, FaFileAlt, FaCheck, FaTimes } from 'react-icons/fa';
import { IoSend, IoCheckmarkDone } from 'react-icons/io5'; 
import { MdInsertEmoticon, MdEdit } from "react-icons/md";

// --- THEME ---
const COLORS = {
  primary: '#005b5e',
  secondary: '#f0f2f5',
  myBubble: '#E3F2FD',
  white: '#ffffff',
  text: '#1a202c',
  online: '#4CAF50',
  unread: '#25D366',
  tickSent: '#757373',
  tickDelivered: '#0d0c0c', 
  tickSeen: '#ebf134' 
};

// --- GLOBAL STYLE ---
const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    padding: 0;
    height: 100%;
    overflow: hidden; 
  }
`;

// --- STYLED COMPONENTS ---
const PageContainer = styled.div`
  display: flex; 
  height: calc(100vh - 64px); 
  background: ${COLORS.secondary};
  overflow: hidden; 
  @media (max-width: 768px) { 
    height: calc(100vh - 64px); 
    flex-direction: row; 
    position: relative;
  }
`;

const Sidebar = styled.div`
  width: 380px; background: white; border-right: 1px solid #e0e0e0;
  display: flex; flex-direction: column;
  overflow: hidden;
  @media (max-width: 768px) { 
    width: 100%; 
    display: ${props => props.$active ? 'none' : 'flex'}; 
  }
`;

const UserHeader = styled.div`
  padding: 15px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
  gap: 12px;
`;

const UserProfileImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${COLORS.primary};
`;

const BrandName = styled.span`
  font-weight: 800;
  color: ${COLORS.primary};
  font-size: 16px;
`;

const ChatWindow = styled.div`
  flex: 1; display: flex; flex-direction: column; background: #f8fafc; position: relative;
  overflow: hidden;
  @media (max-width: 768px) { 
    display: ${props => props.$active ? 'flex' : 'none'}; 
    width: 100%;
    height: 100%;
  }
`;

const SearchBar = styled.div`
  padding: 15px; border-bottom: 1px solid #eee; display: flex; gap: 10px; flex-shrink: 0;
`;

const ChatListArea = styled.div`
  flex: 1;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
`;

const ChatItem = styled.div`
  display: flex; align-items: center; padding: 12px 18px; cursor: pointer;
  background: ${props => props.$selected ? '#f0f7ff' : 'transparent'};
  border-bottom: 1px solid #f1f5f9;
  &:hover { background: #f9fafb; }
`;

const UnreadBadge = styled.div`
  background: ${COLORS.unread}; color: white; border-radius: 50%;
  min-width: 20px; height: 20px; padding: 2px; font-size: 11px;
  display: flex; align-items: center; justify-content: center;
  font-weight: bold; margin-left: 10px;
`;

const MessageBubble = styled.div`
  max-width: 70%; padding: 10px 14px; border-radius: 15px; margin: 4px 0;
  align-self: ${props => props.$isMe ? 'flex-end' : 'flex-start'};
  background: ${props => props.$isMe ? COLORS.primary : COLORS.white};
  color: ${props => props.$isMe ? 'white' : COLORS.text};
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  position: relative;
`;

const MessageArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

// --- UPDATED INPUT AREA FOR MOBILE VISIBILITY ---
const InputArea = styled.div`
  padding: 15px; background: white; display: flex; align-items: center; gap: 12px;
  border-top: 1px solid #eee; flex-shrink: 0;
  z-index: 100; /* Footer-kku mela theriyum */
  
  @media (max-width: 768px) {
    /* Mobile footer maraithal kela gap viduvatharku */
    padding-bottom: 65px; 
    margin-bottom: 10px;
  }
`;

const Modal = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5); display: flex; align-items: center; 
  justify-content: center; z-index: 1000;
`;

const ModalContent = styled.div`
  background: white; padding: 25px; border-radius: 15px; width: 450px; max-height: 85vh; display: flex; flex-direction: column;
`;

const ImagePickerContainer = styled.div`
  display: flex; flex-direction: column; align-items: center; margin-bottom: 20px; position: relative;
`;

const PreviewImage = styled.img`
  width: 80px; height: 80px; border-radius: 50%; object-fit: cover; background: #eee; border: 2px solid ${COLORS.primary};
`;

const ImageOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.9); z-index: 2000;
  display: flex; align-items: center; justify-content: center;
`;

const FullImage = styled.img`
  max-width: 90%; max-height: 90%; border-radius: 5px; box-shadow: 0 0 20px rgba(0,0,0,0.5);
`;

const CloseImageBtn = styled.button`
  position: absolute; top: 20px; right: 20px; background: white; border: none;
  border-radius: 50%; width: 40px; height: 40px; cursor: pointer; display: flex;
  align-items: center; justify-content: center;
`;

const getYouTubeID = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const ChatName = ({ userId, defaultName }) => {
  const [fullName, setFullName] = useState(defaultName);
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "profiles"), where("uid", "==", userId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) setFullName(snapshot.docs[0].data().fullName);
    });
    return () => unsubscribe();
  }, [userId]);
  return <span>{fullName}</span>;
};

const SidebarAvatar = ({ userId, chatPhoto, size = '50px' }) => {
  const [imgUrl, setImgUrl] = useState(chatPhoto);
  useEffect(() => {
    const fetchLatestImg = async () => {
      if (userId) {
        try {
          const q = query(collection(db, "profiles"), where("uid", "==", userId));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            if (userData.profileImageUrl) setImgUrl(userData.profileImageUrl);
          }
        } catch (err) { console.error("Error fetching avatar:", err); }
      } else { setImgUrl(chatPhoto); }
    };
    fetchLatestImg();
  }, [userId, chatPhoto]);

  return (
    <img 
      src={imgUrl || 'https://via.placeholder.com/50'} 
      alt="" 
      style={{ width: size, height: size, borderRadius:'50%', marginRight:'12px', objectFit:'cover', background: '#eee' }} 
      onError={(e) => { e.target.src = 'https://via.placeholder.com/50'; }}
    />
  );
};

const Messages = () => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [newChatSearch, setNewChatSearch] = useState(""); 
  const [uploading, setUploading] = useState(false);
  
  const [groupName, setGroupName] = useState("");
  const [groupImageFile, setGroupImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [editGroupName, setEditGroupName] = useState("");
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState("");

  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(null); 

  const scrollRef = useRef();
  const navigate = useNavigate();

  const notificationAudio = useRef(new Audio(chatNotificationSound));

  const unlockAudio = () => {
    notificationAudio.current.play()
      .then(() => {
        notificationAudio.current.pause();
        notificationAudio.current.currentTime = 0;
        setIsAudioEnabled(true);
      })
      .catch(e => console.log("Audio unlock failed:", e));
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        const q = query(collection(db, "profiles"), where("uid", "==", u.uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
          setUserProfile(snap.docs[0].data());
        }
      }
      else navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "chats"), 
      where("members", "array-contains", user.uid),
      orderBy("lastTimestamp", "desc")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChats(chatList);

      snapshot.docChanges().forEach((change) => {
        if (change.type === "modified") {
          const chatData = change.doc.data();
          const unreadForMe = chatData.unreadCounts?.[user.uid] || 0;
          
          if (unreadForMe > 0 && !snapshot.metadata.hasPendingWrites) {
             notificationAudio.current.play().catch(err => {
               console.log("Audio play blocked by browser.");
             });
          }
        }
      });
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!activeChat || !user) return;
    const q = query(collection(db, "chats", activeChat.id, "messages"), orderBy("timestamp", "asc"));
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
      
      const batch = writeBatch(db);
      let needsUpdate = false;
      
      snap.docs.forEach(msgDoc => {
        const data = msgDoc.data();
        if (data.senderId !== user.uid && data.status !== 'seen') {
          batch.update(msgDoc.ref, { status: 'seen' });
          needsUpdate = true;
        }
      });

      if (needsUpdate) batch.commit();
      
      const clearUnread = async () => {
        await updateDoc(doc(db, "chats", activeChat.id), { [`unreadCounts.${user.uid}`]: 0 });
      };
      clearUnread();
      
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    return () => unsubscribe();
  }, [activeChat, user]); 

  const handleImageChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const startNewChat = async (targetUser) => {
    const chatId = [user.uid, targetUser.uid].sort().join("_");
    const chatRef = doc(db, "chats", chatId);
    const q = query(collection(db, "profiles"), where("uid", "==", user.uid));
    const userSnap = await getDocs(q);
    const currentUserData = !userSnap.empty ? userSnap.docs[0].data() : {};
    
    const chatData = {
      id: chatId,
      members: [user.uid, targetUser.uid],
      memberData: {
        [user.uid]: { name: currentUserData.fullName || user.displayName || "User", photo: currentUserData.profileImageUrl || user.photoURL || '' },
        [targetUser.uid]: { name: targetUser.fullName, photo: targetUser.profileImageUrl || '' }
      },
      unreadCounts: { [user.uid]: 0, [targetUser.uid]: 0 },
      type: 'private',
      lastTimestamp: serverTimestamp()
    };
    await setDoc(chatRef, chatData, { merge: true });
    setActiveChat({ id: chatId, ...chatData });
    setShowNewChatModal(false);
    setNewChatSearch(""); 
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeChat) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `chatMedia/${activeChat.id}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      let type = 'file';
      if (file.type.startsWith('image/')) type = 'image';
      else if (file.type.startsWith('video/')) type = 'video';

      await handleSend(url, type, file.name);
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload file.");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) {
      alert("Please enter group name and select at least one member.");
      return;
    }
    setIsCreating(true);
    try {
      let finalImageUrl = "";
      if (groupImageFile) {
        const storageRef = ref(storage, `groupProfiles/${Date.now()}_${groupImageFile.name}`);
        const uploadTask = await uploadBytes(storageRef, groupImageFile);
        finalImageUrl = await getDownloadURL(uploadTask.ref);
      }

      const q = query(collection(db, "profiles"), where("uid", "==", user.uid));
      const userSnap = await getDocs(q);
      const currentUserData = !userSnap.empty ? userSnap.docs[0].data() : {};

      const memberIds = [user.uid, ...selectedMembers.map(m => m.uid)];
      
      const memberDataMap = {
        [user.uid]: { 
          name: currentUserData.fullName || user.displayName || "User", 
          photo: currentUserData.profileImageUrl || user.photoURL || '' 
        }
      };
      selectedMembers.forEach(m => {
        memberDataMap[m.uid] = { name: m.fullName, photo: m.profileImageUrl || '' };
      });

      const groupData = {
        groupName,
        groupPhoto: finalImageUrl,
        members: memberIds,
        memberData: memberDataMap,
        admins: [user.uid],
        createdBy: user.uid,
        type: 'group',
        lastMessage: "Group created",
        lastTimestamp: serverTimestamp(),
        unreadCounts: memberIds.reduce((acc, id) => ({ ...acc, [id]: 0 }), {})
      };
      await addDoc(collection(db, "chats"), groupData);
      setShowGroupModal(false);
      setGroupName("");
      setGroupImageFile(null);
      setImagePreview(null);
      setSelectedMembers([]);
    } catch (error) { console.error(error); } finally { setIsCreating(false); }
  };

  const updateGroupProfile = async (file) => {
    if (!activeChat || !activeChat.admins.includes(user.uid)) return;
    try {
      const storageRef = ref(storage, `groupProfiles/${Date.now()}_${file.name}`);
      const uploadTask = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(uploadTask.ref);
      await updateDoc(doc(db, "chats", activeChat.id), { groupPhoto: url });
      setActiveChat({...activeChat, groupPhoto: url});
    } catch (error) { console.error(error); }
  };

  const handleUpdateGroupName = async () => {
    if (!editGroupName.trim() || !activeChat.admins.includes(user.uid)) return;
    await updateDoc(doc(db, "chats", activeChat.id), { groupName: editGroupName });
    setActiveChat({...activeChat, groupName: editGroupName});
    setEditGroupName("");
  };

  const handleAddMembers = async (newMembers) => {
    if (!activeChat.admins.includes(user.uid)) {
        alert("Only admins can add members.");
        return;
    }
    const updatedMembers = [...activeChat.members, ...newMembers.map(m => m.uid)];
    const newMemberData = { ...activeChat.memberData };
    newMembers.forEach(m => {
        newMemberData[m.uid] = { name: m.fullName, photo: m.profileImageUrl || '' };
    });
    const newUnread = { ...activeChat.unreadCounts };
    newMembers.forEach(m => newUnread[m.uid] = 0);
    
    await updateDoc(doc(db, "chats", activeChat.id), { 
      members: updatedMembers,
      memberData: newMemberData,
      unreadCounts: newUnread
    });
    setActiveChat({...activeChat, members: updatedMembers, memberData: newMemberData});
    setShowAddMembersModal(false);
    setSelectedMembers([]);
    setAddMemberSearch("");
  };

  const handleRemoveMember = async (targetUid) => {
    if (!activeChat.admins.includes(user.uid)) {
        alert("Only admins can remove members.");
        return;
    }
    if (targetUid === user.uid) {
        alert("You cannot remove yourself. Use 'Leave Group' instead.");
        return;
    }
    if (window.confirm("Are you sure you want to remove this member?")) {
        const updatedMembers = activeChat.members.filter(id => id !== targetUid);
        const updatedAdmins = activeChat.admins.filter(id => id !== targetUid);
        const updatedMemberData = { ...activeChat.memberData };
        delete updatedMemberData[targetUid];
        const updatedUnread = { ...activeChat.unreadCounts };
        delete updatedUnread[targetUid];

        await updateDoc(doc(db, "chats", activeChat.id), { 
            members: updatedMembers,
            admins: updatedAdmins,
            memberData: updatedMemberData,
            unreadCounts: updatedUnread
        });
        setActiveChat({...activeChat, members: updatedMembers, admins: updatedAdmins, memberData: updatedMemberData});
    }
  };

  const toggleAdmin = async (targetUid) => {
    if (!activeChat.admins.includes(user.uid)) return;
    let newAdmins = [...activeChat.admins];
    if (newAdmins.includes(targetUid)) {
      if (newAdmins.length <= 1) return alert("Group must have at least one admin");
      newAdmins = newAdmins.filter(id => id !== targetUid);
    } else {
      if (newAdmins.length >= 5) return alert("Maximum 5 admins allowed");
      newAdmins.push(targetUid);
    }
    await updateDoc(doc(db, "chats", activeChat.id), { admins: newAdmins });
    setActiveChat({...activeChat, admins: newAdmins});
  };

  const handleDeleteGroup = async () => {
    if (!activeChat.admins.includes(user.uid)) return;
    if (window.confirm("Are you sure you want to delete this group?")) {
      await deleteDoc(doc(db, "chats", activeChat.id));
      setActiveChat(null);
      setShowGroupInfo(false);
    }
  };

  const handleSend = async (mediaUrl = null, type = 'text', fileName = '') => {
    if (!inputText.trim() && !mediaUrl) return;
    
    let finalType = type;
    const youtubeId = getYouTubeID(inputText);
    if (youtubeId) finalType = 'youtube';

    const msgData = { 
      senderId: user.uid, 
      text: inputText, 
      mediaUrl, 
      fileName,
      type: finalType, 
      timestamp: serverTimestamp(),
      status: 'delivered' 
    };
    
    const tempText = inputText;
    setInputText("");
    await addDoc(collection(db, "chats", activeChat.id, "messages"), msgData);
    
    let lastMsgPreview = "Attachment";
    if (finalType === 'text') lastMsgPreview = tempText;
    else if (finalType === 'youtube') lastMsgPreview = "YouTube Video";

    const updateData = { lastMessage: lastMsgPreview, lastTimestamp: serverTimestamp() };
    activeChat.members.forEach(id => {
      if (id !== user.uid) updateData[`unreadCounts.${id}`] = increment(1);
    });
    await updateDoc(doc(db, "chats", activeChat.id), updateData);
  };

  const fetchProfiles = async (mode) => {
    const snap = await getDocs(collection(db, "profiles"));
    const profiles = snap.docs.map(doc => doc.data()).filter(p => p.uid !== user.uid);
    setAllProfiles(profiles);
    setSelectedMembers([]);
    if (mode === 'group') setShowGroupModal(true);
    else if (mode === 'add_members') {
      setAddMemberSearch("");
      setShowAddMembersModal(true);
    }
    else {
      setNewChatSearch(""); 
      setShowNewChatModal(true);
    }
  };

  const getOtherMember = (chat) => {
    if (chat.type === 'group') return { name: chat.groupName, photo: chat.groupPhoto, id: null };
    const otherId = chat.members.find(id => id !== user.uid);
    const data = chat.memberData ? chat.memberData[otherId] : {};
    return { ...data, id: otherId };
  };

  const isAdmin = activeChat?.admins?.includes(user?.uid);

  const renderTicks = (status) => {
    const tickStyle = { marginLeft: '4px', stroke: 'currentColor', strokeWidth: '16px' };
    if (status === 'seen') return <IoCheckmarkDone size={17} style={{ ...tickStyle, color: COLORS.tickSeen }} />;
    if (status === 'delivered') return <IoCheckmarkDone size={17} style={{ ...tickStyle, color: COLORS.tickDelivered }} />;
    return <FaCheck size={10} style={{ marginLeft: '4px', color: COLORS.tickSent, opacity: 0.8 }} />;
  };

  return (
    <>
      <GlobalStyle />
      <AppHeader />
      
      {fullScreenImage && (
        <ImageOverlay onClick={() => setFullScreenImage(null)}>
          <CloseImageBtn onClick={() => setFullScreenImage(null)}><FaTimes size={20} /></CloseImageBtn>
          <FullImage src={fullScreenImage} onClick={(e) => e.stopPropagation()} />
        </ImageOverlay>
      )}

      <PageContainer onClick={unlockAudio}>
        <Sidebar $active={!!activeChat}>
          <UserHeader>
             <UserProfileImg 
               src={userProfile?.profileImageUrl || 'https://via.placeholder.com/40'} 
               alt="User Profile" 
               onError={(e) => { e.target.src = 'https://via.placeholder.com/40'; }}
             />
             <BrandName>Avaami Business Connect</BrandName>
          </UserHeader>

          <SearchBar>
            <div style={{flex:1, background:'#f1f5f9', display:'flex', alignItems:'center', padding:'8px 12px', borderRadius:'20px'}}>
              <FaSearch color="#64748b" />
              <input placeholder="Search chats..." style={{border:'none', background:'transparent', outline:'none', marginLeft:'8px', width:'100%'}} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <button onClick={() => fetchProfiles('private')} title="New Chat" style={{border:'none', background:COLORS.primary, color:'white', borderRadius:'50%', width:'40px', height:'40px', cursor:'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><FaPlus /></button>
            <button onClick={() => fetchProfiles('group')} title="New Group" style={{border:'none', background:'#10b981', color:'white', borderRadius:'50%', width:'40px', height:'40px', cursor:'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><FaUsers /></button>
          </SearchBar>

          <ChatListArea>
            {chats.filter(c => getOtherMember(c).name?.toLowerCase().includes(searchQuery.toLowerCase())).map(chat => {
              const info = getOtherMember(chat);
              const unreadCount = chat.unreadCounts?.[user.uid] || 0;
              return (
                <ChatItem key={chat.id} $selected={activeChat?.id === chat.id} onClick={() => setActiveChat(chat)}>
                  <SidebarAvatar userId={info.id} chatPhoto={info.photo} />
                  <div style={{flex:1}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                      <span style={{fontWeight:'bold', color:COLORS.text}}>
                        {chat.type === 'group' ? info.name : <ChatName userId={info.id} defaultName={info.name} />}
                      </span>
                      {unreadCount > 0 && <UnreadBadge>{unreadCount}</UnreadBadge>}
                    </div>
                    <div style={{fontSize:'12px', color: unreadCount > 0 ? COLORS.unread : '#64748b', fontWeight: unreadCount > 0 ? '600' : 'normal', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'200px'}}>{chat.lastMessage}</div>
                  </div>
                </ChatItem>
              );
            })}
          </ChatListArea>
        </Sidebar>

        <ChatWindow $active={!!activeChat}>
          {activeChat ? (
            <>
              <div style={{padding:'12px 20px', background:'white', display:'flex', alignItems:'center', borderBottom:'1px solid #eee', flexShrink: 0}}>
                <FaArrowLeft onClick={() => setActiveChat(null)} style={{marginRight:'15px', cursor:'pointer'}} />
                <div onClick={() => activeChat.type === 'group' && setShowGroupInfo(true)} style={{display:'flex', alignItems:'center', cursor: activeChat.type === 'group' ? 'pointer' : 'default', flex:1}}>
                    <SidebarAvatar userId={getOtherMember(activeChat).id} chatPhoto={getOtherMember(activeChat).photo} size="35px" />
                    <div style={{fontWeight:'bold'}}>
                        {activeChat.type === 'group' ? getOtherMember(activeChat).name : <ChatName userId={getOtherMember(activeChat).id} defaultName={getOtherMember(activeChat).name} />}
                    </div>
                </div>
                {activeChat.type === 'group' && <FaEllipsisV onClick={() => setShowGroupInfo(true)} style={{cursor:'pointer', color:'#64748b'}} />}
              </div>

              <MessageArea>
                {messages.map((m, i) => {
                  const isMe = m.senderId === user.uid;
                  const youtubeId = getYouTubeID(m.text);

                  return (
                    <MessageBubble key={i} $isMe={isMe}>
                      {activeChat.type === 'group' && !isMe && (
                          <div style={{fontSize:'10px', fontWeight:'bold', color:COLORS.primary, marginBottom:'4px'}}>
                              <ChatName userId={m.senderId} defaultName={activeChat.memberData?.[m.senderId]?.name || "User"} />
                          </div>
                      )}
                      
                      {m.type === 'image' && (
                        <img 
                          src={m.mediaUrl} 
                          onClick={() => setFullScreenImage(m.mediaUrl)} 
                          style={{maxWidth:'100%', borderRadius:'8px', cursor:'zoom-in', marginBottom:'5px'}} 
                          alt=""
                        />
                      )}

                      {m.type === 'video' && (
                        <video controls style={{maxWidth:'100%', borderRadius:'8px', marginBottom:'5px'}}>
                          <source src={m.mediaUrl} type="video/mp4" />
                        </video>
                      )}

                      {m.type === 'file' && (
                        <a href={m.mediaUrl} target="_blank" rel="noreferrer" style={{display:'flex', alignItems:'center', gap:'10px', textDecoration:'none', color: isMe ? 'white' : COLORS.primary, background:'rgba(0,0,0,0.05)', padding:'8px', borderRadius:'8px'}}>
                          <FaFileAlt /> <span style={{fontSize:'12px'}}>{m.fileName || "View Document"}</span>
                        </a>
                      )}

                      {youtubeId && (
                        <div style={{marginTop:'5px', marginBottom:'8px', width: '100%', overflow: 'hidden'}}>
                          <div style={{position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0}}>
                            <iframe 
                                style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '8px'}}
                                src={`https://www.youtube.com/embed/${youtubeId}`} 
                                title="YouTube video player" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowFullScreen
                            ></iframe>
                          </div>
                        </div>
                      )}

                      <div style={{wordBreak:'break-word'}}>
                        {m.text && m.text.startsWith('http') ? (
                          <a href={m.text} target="_blank" rel="noreferrer" style={{color: isMe ? '#E3F2FD' : '#4A90E2', textDecoration: 'underline'}}>
                            {m.text}
                          </a>
                        ) : (
                          m.text
                        )}
                      </div>
                      
                      <div style={{fontSize:'10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop:'5px', opacity:0.9}}>
                        {m.timestamp?.toDate().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                        {isMe && renderTicks(m.status)}
                      </div>
                    </MessageBubble>
                  );
                })}
                <div ref={scrollRef} />
              </MessageArea>

              {uploading && <div style={{padding:'5px 20px', fontSize:'12px', color:COLORS.primary}}>Uploading file...</div>}

              {showEmoji && <div style={{position:'absolute', bottom:'80px', left:'20px', zIndex:50}}><EmojiPicker onEmojiClick={(e) => setInputText(t => t + e.emoji)} /></div>}
              
              <InputArea>
                <MdInsertEmoticon size={26} onClick={() => setShowEmoji(!showEmoji)} style={{cursor:'pointer', color:'#64748b'}} />
                <label style={{cursor:'pointer', display:'flex', alignItems:'center'}}>
                  <FaPaperclip size={20} color="#64748b" />
                  <input type="file" hidden onChange={handleFileUpload} />
                </label>
                <input 
                  value={inputText} 
                  onChange={(e) => setInputText(e.target.value)} 
                  placeholder="Type a message..." 
                  style={{flex:1, padding:'12px', borderRadius:'25px', border:'1px solid #e2e8f0', outline:'none'}} 
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()} 
                />
                <IoSend size={24} color={COLORS.primary} onClick={() => handleSend()} style={{cursor:'pointer'}} />
              </InputArea>

              {showGroupInfo && (
                <div style={{position:'absolute', right:0, top:0, bottom:0, width:'320px', background:'white', zIndex:100, borderLeft:'1px solid #eee', display:'flex', flexDirection:'column', boxShadow:'-2px 0 5px rgba(0,0,0,0.1)'}}>
                    <div style={{padding:'20px', borderBottom:'1px solid #eee', display:'flex', alignItems:'center', flexShrink: 0}}>
                        <FaArrowLeft onClick={() => setShowGroupInfo(false)} style={{marginRight:'15px', cursor:'pointer'}} />
                        <span style={{fontWeight:'bold'}}>Group Info</span>
                    </div>
                    <div style={{overflowY:'auto', flex:1, padding:'20px'}}>
                        <div style={{textAlign:'center', marginBottom:'20px'}}>
                            <div style={{position:'relative', display:'inline-block'}}>
                                <img 
                                  src={activeChat.groupPhoto || 'https://via.placeholder.com/100'} 
                                  onClick={() => activeChat.groupPhoto && setFullScreenImage(activeChat.groupPhoto)}
                                  style={{width:'100px', height:'100px', borderRadius:'50%', objectFit:'cover', cursor: activeChat.groupPhoto ? 'zoom-in' : 'default'}} 
                                  alt=""
                                />
                                {isAdmin && (
                                    <label style={{position:'absolute', bottom:0, right:0, background:COLORS.primary, color:'white', padding:'6px', borderRadius:'50%', cursor:'pointer', display: 'flex'}}>
                                        <FaCamera size={12}/>
                                        <input type="file" hidden accept="image/*" onChange={(e) => updateGroupProfile(e.target.files[0])} />
                                    </label>
                                )}
                            </div>
                            <h3 style={{marginTop:'10px'}}>{activeChat.groupName}</h3>
                            <p style={{fontSize:'12px', color:'#64748b'}}>{activeChat.members.length} Members</p>
                        </div>

                        {isAdmin && (
                            <div style={{marginBottom:'20px'}}>
                                <div style={{display:'flex', gap:'5px'}}>
                                    <input placeholder="New Group Name" value={editGroupName} onChange={(e) => setEditGroupName(e.target.value)} style={{flex:1, padding:'8px', borderRadius:'5px', border:'1px solid #ddd'}} />
                                    <button onClick={handleUpdateGroupName} style={{background:COLORS.primary, color:'white', border:'none', borderRadius:'5px', padding:'0 10px', display: 'flex', alignItems: 'center'}}><MdEdit/></button>
                                </div>
                                <button onClick={() => fetchProfiles('add_members')} style={{width:'100%', marginTop:'10px', padding:'10px', background:'#f1f5f9', border:'none', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', cursor:'pointer'}}>
                                    <FaUserPlus/> Add Members
                                </button>
                            </div>
                        )}

                        <div style={{fontWeight:'bold', marginBottom:'10px', fontSize:'14px'}}>Members</div>
                        {activeChat.members.map(mid => (
                            <div key={mid} style={{display:'flex', alignItems:'center', padding:'8px 0'}}>
                                <SidebarAvatar userId={mid} size="35px" />
                                <div style={{flex:1, fontSize:'13px'}}>
                                    {mid === user.uid ? "You" : <ChatName userId={mid} defaultName={activeChat.memberData?.[mid]?.name || "User"} />}
                                    {activeChat.admins.includes(mid) && <span style={{marginLeft:'5px', fontSize:'10px', color:COLORS.primary, border:'1px solid', padding:'1px 4px', borderRadius:'4px'}}>Admin</span>}
                                </div>
                                {isAdmin && mid !== user.uid && (
                                    <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                                        <FaUserShield onClick={() => toggleAdmin(mid)} style={{cursor:'pointer', color: activeChat.admins.includes(mid) ? COLORS.primary : '#cbd5e1'}} title="Toggle Admin" />
                                        <FaUserMinus onClick={() => handleRemoveMember(mid)} style={{cursor:'pointer', color:'#ef4444'}} title="Remove Member" />
                                    </div>
                                )}
                            </div>
                        ))}

                        {isAdmin && (
                            <button onClick={handleDeleteGroup} style={{width:'100%', marginTop:'30px', padding:'12px', background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', cursor:'pointer', fontWeight:'bold'}}>
                                <FaTrash/> Delete Group
                            </button>
                        )}
                    </div>
                </div>
              )}
            </>
          ) : (
            <div style={{margin:'auto', textAlign:'center', color:'#94a3b8'}}>
               <FaUsers size={50} style={{marginBottom:'10px'}} />
               <h3>Avaami Business Messaging</h3>
               <p>Select a chat to start messaging.</p>
            </div>
          )}
        </ChatWindow>
      </PageContainer>

      {/* MODALS */}
      {showNewChatModal && (
        <Modal onClick={() => { setShowNewChatModal(false); setNewChatSearch(""); }}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h3 style={{marginBottom:'15px'}}>New Message</h3>
            <div style={{background:'#f8fafc', padding:'8px', borderRadius:'8px', display:'flex', alignItems:'center', marginBottom:'10px'}}>
              <FaSearch size={14} color="#64748b" />
              <input placeholder="Search people..." value={newChatSearch} onChange={(e) => setNewChatSearch(e.target.value)} style={{border:'none', background:'transparent', marginLeft:'8px', outline:'none', width:'100%'}} />
            </div>
            <div style={{overflowY:'auto', flex:1}}>
              {allProfiles.filter(p => p.fullName?.toLowerCase().includes(newChatSearch.toLowerCase())).map(p => (
                <ChatItem key={p.uid} onClick={() => startNewChat(p)}>
                  <img src={p.profileImageUrl || 'https://via.placeholder.com/40'} style={{width:'40px', height:'40px', borderRadius:'50%', marginRight:'10px', objectFit: 'cover'}} alt="" />
                  <div><div style={{fontWeight:'bold'}}>{p.fullName}</div><div style={{fontSize:'12px'}}>{p.mainRole}</div></div>
                </ChatItem>
              ))}
            </div>
          </ModalContent>
        </Modal>
      )}

      {showGroupModal && (
        <Modal onClick={() => setShowGroupModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h3 style={{marginBottom:'15px'}}>Create New Group</h3>
            <ImagePickerContainer>
              <PreviewImage src={imagePreview || 'https://via.placeholder.com/80'} alt="Preview" />
              <label style={{ position:'absolute', bottom:0, right:'40%', background:COLORS.primary, color:'white', padding:'6px', borderRadius:'50%', cursor:'pointer', display:'flex' }}>
                <FaCamera size={14} /><input type="file" hidden accept="image/*" onChange={(e) => handleImageChange(e, setGroupImageFile, setImagePreview)} />
              </label>
            </ImagePickerContainer>
            <input placeholder="Group Name" value={groupName} onChange={(e) => setGroupName(e.target.value)} style={{padding:'10px', marginBottom:'10px', borderRadius:'8px', border:'1px solid #ddd'}} />
            <div style={{background:'#f8fafc', padding:'8px', borderRadius:'8px', display:'flex', alignItems:'center', marginBottom:'10px'}}>
              <FaSearch size={14} color="#64748b" /><input placeholder="Search members..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} style={{border:'none', background:'transparent', marginLeft:'8px', outline:'none', width:'100%'}} />
            </div>
            <div style={{overflowY:'auto', flex:1, marginBottom:'15px'}}>
              {allProfiles.filter(p => p.fullName?.toLowerCase().includes(memberSearch.toLowerCase())).map(p => {
                const isSelected = selectedMembers.find(m => m.uid === p.uid);
                return (
                  <ChatItem key={p.uid} onClick={() => isSelected ? setSelectedMembers(selectedMembers.filter(m => m.uid !== p.uid)) : setSelectedMembers([...selectedMembers, p])}>
                    <img src={p.profileImageUrl || 'https://via.placeholder.com/40'} style={{width:'40px', height:'40px', borderRadius:'50%', marginRight:'10px', objectFit: 'cover'}} alt="" />
                    <div style={{flex:1}}><div style={{fontWeight:'bold'}}>{p.fullName}</div><div style={{fontSize:'12px'}}>{p.mainRole}</div></div>
                    {isSelected ? <FaCheckCircle color={COLORS.primary} size={20} /> : <FaRegCircle color="#cbd5e1" size={20} />}
                  </ChatItem>
                );
              })}
            </div>
            <button disabled={isCreating} onClick={handleCreateGroup} style={{background: isCreating ? '#94a3b8' : COLORS.primary, color:'white', border:'none', padding:'12px', borderRadius:'8px', fontWeight:'bold', cursor:'pointer'}}>{isCreating ? "Creating..." : "Create Group"}</button>
          </ModalContent>
        </Modal>
      )}

      {showAddMembersModal && (
        <Modal onClick={() => { setShowAddMembersModal(false); setAddMemberSearch(""); }}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h3 style={{marginBottom:'15px'}}>Add New Members</h3>
            <div style={{background:'#f8fafc', padding:'8px', borderRadius:'8px', display:'flex', alignItems:'center', marginBottom:'10px'}}>
              <FaSearch size={14} color="#64748b" /><input placeholder="Search members to add..." value={addMemberSearch} onChange={(e) => setAddMemberSearch(e.target.value)} style={{border:'none', background:'transparent', marginLeft:'8px', outline:'none', width:'100%'}} />
            </div>
            <div style={{overflowY:'auto', flex:1}}>
              {allProfiles
                .filter(p => !activeChat.members.includes(p.uid))
                .filter(p => p.fullName?.toLowerCase().includes(addMemberSearch.toLowerCase()))
                .map(p => {
                const isSelected = selectedMembers.find(m => m.uid === p.uid);
                return (
                  <ChatItem key={p.uid} onClick={() => isSelected ? setSelectedMembers(selectedMembers.filter(m => m.uid !== p.uid)) : setSelectedMembers([...selectedMembers, p])}>
                    <img src={p.profileImageUrl || 'https://via.placeholder.com/40'} style={{width:'40px', height:'40px', borderRadius:'50%', marginRight:'10px', objectFit: 'cover'}} alt="" />
                    <div style={{flex:1}}><div style={{fontWeight:'bold'}}>{p.fullName}</div></div>
                    {isSelected ? <FaCheckCircle color={COLORS.primary} size={20} /> : <FaRegCircle color="#cbd5e1" size={20} />}
                  </ChatItem>
                );
              })}
            </div>
            <button onClick={() => handleAddMembers(selectedMembers)} style={{background:COLORS.primary, color:'white', border:'none', padding:'12px', borderRadius:'8px', marginTop:'15px', fontWeight:'bold', cursor:'pointer'}}>Add Selected</button>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default Messages;